import React from "react";
import type { Section } from "@repo/backend/schema/siteConfig";
import type { FeatureFlags } from "@repo/backend/schema/clientConfig";
import { lookupSection } from "./registry.js";
import { SectionAnimator } from "./animations/SectionAnimator.js";

interface SectionRendererProps {
  sections: Section[];
  clientId: string;
  features?: Partial<FeatureFlags>;
}

function UnknownSection({ type, variant }: { type: string; variant: string }) {
  if (import.meta.env.DEV) {
    return (
      <div
        style={{
          padding: "2rem",
          border: "2px dashed #f97316",
          borderRadius: "0.5rem",
          margin: "1rem 0",
          color: "#92400e",
          fontSize: "0.875rem",
          fontFamily: "monospace",
        }}
      >
        Unregistered section: <strong>{type}/{variant}</strong> — register via{" "}
        <code>registerSection("{type}", "{variant}", YourComponent)</code>
      </div>
    );
  }
  return null;
}

export function SectionRenderer({ sections, clientId, features = {} }: SectionRendererProps) {
  return (
    <>
      {sections.map((section) => {
        if (section.visibleWhen) {
          const flag = section.visibleWhen.feature as keyof FeatureFlags;
          if (!features[flag]) return null;
        }

        const Component = lookupSection(section.type, section.variant);

        if (!Component) {
          return (
            <UnknownSection
              key={section.id}
              type={section.type}
              variant={section.variant}
            />
          );
        }

        return (
          <SectionAnimator
            key={section.id}
            animation={section.animation}
            sectionId={section.id}
          >
            <Component props={section.props} clientId={clientId} />
          </SectionAnimator>
        );
      })}
    </>
  );
}
